import { useFormik } from "formik";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import TextFiled from "../../components/common/InputField/TextFiled";
import MainButton from "../../components/common/MainButton/MainButton";
import PasswordField from "../../components/common/PasswordField/PasswordField";
import Heading from "../../components/common/Typography/Heading";
import AuxiliaryText from "../../components/common/Typography/AuxiliaryText";
import Spinner from "../../components/common/Spinner";
import { POPUP_FORM_CONTENT_WIDTH_CLASS } from "../../components/popup/popupLayoutSpec";
import {
  InitialValues,
  validationSchema,
} from "../../FormValidations/SignInSchema";
import useAuth from "../../Hooks/useAuth";
import { useGraphQL } from "../../context/GraphQLProvider";
import { LoginQuery } from "../../services/LoginService";
import { useDispatch, useSelector } from "react-redux";
import { clearRedirectPath } from '../../Redux/Slice/authRedirectSlice';
import { setUserDetails } from '../../Redux/Slice/userSlice';
import {
  createAdminSessionForLogin,
  setAdminSession,
} from '../../Redux/Slice/adminAccessSlice';
import { attachAdminSessionToLoginPayload } from '../../utils/rolePermissions';
import {
  checkSuspiciousActivity,
  detectAutomation,
  logSecurityEvent,
  getClientFingerprint
} from "../../utils/adminSecurity";

const AdminLogin = () => {
  const graphqlService = useGraphQL();
  const [PasswordType, setPasswordType] = useState("true");
  const [RequestStart, setRequetStart] = useState(false);
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [loginRestricted, setLoginRestricted] = useState(false); // Enable login
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const redirectPath = useSelector(state => state.authRedirect.redirectPath);

        // Security checks on mount
        useEffect(() => {
          // Allow rendering for /admin URL
          setIsValidUrl(true);
          
          logSecurityEvent('admin_login_page_access', {
            pathname: location.pathname,
            fingerprint: getClientFingerprint()
          });

          // Check for suspicious activity
          if (checkSuspiciousActivity()) {
            logSecurityEvent('suspicious_user_agent', {
              userAgent: navigator.userAgent
            });
          }

          // Check for automation
          if (detectAutomation()) {
            logSecurityEvent('automation_detected', {});
            toast.error("Automated access detected. Please use a regular browser.");
          }
        }, [location.pathname]);

  // Helper function to reset all state variables
  const resetAllState = () => {
    setRequetStart(false);
    setPasswordType("true");
    formik.resetForm();
  };

  const LoginUser = async (gql, email, password) => {
    try {
      setRequetStart(true);
      
      // Check for dummy superadmin credentials first
      if (email === 'superadmin@gmail.com' && password === 'Passwork#000') {
        console.log('🔐 Mock Superadmin Login Detected - Development Mode');
        
        // Add realistic delay for mock login
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Create mock superadmin user data
        const mockUser = {
          access_token: 'mock_admin_token_' + Date.now(),
          id_token: 'mock_id_token',
          scope: 'admin',
          expires_in: Date.now() + (24 * 60 * 60 * 1000), // 24 hours from now
          token_type: 'Bearer',
          id: 'admin_001',
          personalEmail: 'superadmin@gmail.com',
          profilePicture: 'https://tradeposter.blob.core.windows.net/tradeposter/images/Profile-image-1750371310119.jpg',
          firstName: 'Super',
          lastName: 'Admin',
          businessName: 'TradePoster Admin',
          roleType: 'superadmin',
          primaryContactNumber: '+61 400 000 000',
          secondaryContactNumber: '+61 400 000 001',
          emergencyContact: '+61 400 000 002',
          jobTitle: 'System Administrator',
          location: 'Australia',
          department: 'IT Administration',
          startDate: '2024-01-01',
          tradesPosterEmail: 'superadmin@tradeposter.com',
          abn: '12345678901',
          companyName: 'TradePoster Pty Ltd',
          serviceCategory: {
            id: 'admin_cat',
            name: 'Administration'
          },
          userPlan: {
            id: 'admin_plan',
            userId: 'admin_001',
            planId: 'unlimited',
            startDate: '2024-01-01',
            trialEndDate: null,
            isTrialActive: false,
            isPaidActive: true,
            plan: {
              id: 'unlimited',
              name: 'Unlimited Admin Plan',
              price: 0,
              keywordsLimit: -1,
              postsLimit: -1,
              portfolioType: 'unlimited',
              visibilityBoost: true,
              prioritySearch: true,
              analytics: true,
              certifications: true,
              successStories: true,
              trialDays: 0
            }
          }
        };

        const adminSession = createAdminSessionForLogin(mockUser);
        const userDetails = attachAdminSessionToLoginPayload(
          {
            accessToken: mockUser.access_token,
            roleType: mockUser.roleType,
            expireAt: mockUser.expires_in,
            user: mockUser,
            personalEmail: mockUser.personalEmail,
            firstName: mockUser.firstName,
            lastName: mockUser.lastName,
            location: mockUser.location,
            abn: mockUser.abn,
            businessName: mockUser.businessName,
            companyName: mockUser.companyName,
            profilePicture: mockUser.profilePicture,
            startDate: mockUser.startDate,
            serviceCategory: mockUser.serviceCategory,
            userPlan: mockUser.userPlan,
          },
          adminSession,
        );

        localStorage.setItem("LoginSession", JSON.stringify(userDetails));

        logSecurityEvent('admin_login_success', {
          email,
          roleType: mockUser.roleType,
          isMockUser: true,
        });

        setAuth(userDetails);
        dispatch(setUserDetails(userDetails));
        dispatch(setAdminSession(adminSession));
        
        // Navigate to admin dashboard
        const targetPath = redirectPath || '/admin/dashboard';
        navigate(targetPath);
        dispatch(clearRedirectPath());
        
        resetAllState();
        return;
      }
      
      // For real users, use the actual GraphQL login
      const response = await LoginQuery(gql, { email, password });
      
      if (response?.loginUser?.access_token) {
        const user = response.loginUser;
        
        // Check if user has superadmin role
        if (user.roleType !== 'superadmin') {
          toast.error("Access denied. Only super administrators can access this area.");
          setRequetStart(false);
          logSecurityEvent('admin_login_unauthorized_role', { 
            email, 
            roleType: user.roleType 
          });
          return;
        }
        
        const adminSession = createAdminSessionForLogin(user);
        const userDetails = attachAdminSessionToLoginPayload(
          {
            accessToken: user.access_token,
            roleType: user.roleType,
            expireAt: user.expires_in,
            user,
            personalEmail: user.personalEmail,
            firstName: user.firstName,
            lastName: user.lastName,
            location: user.location,
            abn: user.abn,
            businessName: user.businessName,
            companyName: user.companyName,
            profilePicture: user.profilePicture,
            startDate: user.startDate,
            serviceCategory: user.serviceCategory,
            userPlan: user.userPlan,
          },
          adminSession,
        );

        localStorage.setItem("LoginSession", JSON.stringify(userDetails));

        logSecurityEvent('admin_login_success', {
          email,
          roleType: user.roleType,
        });

        setAuth(userDetails);
        dispatch(setUserDetails(userDetails));
        dispatch(setAdminSession(adminSession));
        
        // Navigate to admin dashboard
        const targetPath = redirectPath || '/admin/dashboard';
        navigate(targetPath);
        dispatch(clearRedirectPath());
        
        resetAllState();
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error("Invalid credentials or network error");
      setRequetStart(false);
      
      logSecurityEvent('admin_login_failed', { email });
    }
  };

  const formik = useFormik({
    initialValues: InitialValues,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      LoginUser(graphqlService, values.email, values.password);
    },
  });

  const handleLogin = () => {
    // Check if login is temporarily restricted - silently block
    if (loginRestricted) {
      return;
    }
    
    if (formik.values.email && formik.values.password && !formik.errors.email && !formik.errors.password) {
      formik.handleSubmit();
    } else {
      formik.setFieldTouched('email', true);
      formik.setFieldTouched('password', true);
    }
  };

  // Don't render anything until URL is validated
  if (!isValidUrl) {
    return null;
  }

  return (
    <>
      <ToastContainer autoClose={2000} />
             <div className="min-h-screen bg-white flex items-center justify-center">
               {RequestStart && <Spinner />}
               <div className={`${POPUP_FORM_CONTENT_WIDTH_CLASS} mx-auto relative`}>
          <div className="w-full">
            <form>
              {/* Email field */}
              <div className="flex justify-center w-full mb-6">
                <div className="w-full mx-auto">
                  <TextFiled
                    className={"tp-fields"}
                    label={"Email"}
                    variant={"medium"}
                    fullWidth
                    type="email"
                    name="email"
                    placeholder={"Enter your registered email"}
                    onChange={formik?.handleChange}
                    value={formik?.values?.email}
                    error={formik.touched.email && formik.errors.email ? formik.errors.email : null}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleLogin();
                      }
                    }}
                  />
                </div>
              </div>

              {/* Password field */}
              <div className="flex justify-center w-full">
                <div className="w-full mx-auto">
                  <label className="mb-2 block">
                    <AuxiliaryText
                      variant={"FieldLabel"}
                      className={"text-offblack"}
                    >
                      Password
                    </AuxiliaryText>
                  </label>
                  <PasswordField
                    fullWidth
                    passwordtype={PasswordType}
                    setpasswordtype={setPasswordType}
                    name="password"
                    placeholder={"Enter your associated password"}
                    onChange={formik?.handleChange}
                    value={formik?.values?.password}
                    className={"tp-fields"}
                    error={formik.touched.password && formik.errors.password ? formik.errors.password : null}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleLogin();
                      }
                    }}
                  />
                </div>
              </div>

              {/* Login button */}
              <div className="flex justify-center w-full">
                <div className="w-full mx-auto">
                  <div className="flex items-center justify-end mt-10 w-full">
                    <MainButton
                      variant={"small"}
                      onClick={handleLogin}
                      disabled={loginRestricted}
                    >
                      Login
                    </MainButton>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminLogin;
